/**
 * External dependencies
 */
import React, { useEffect, useRef, useState, forwardRef } from 'react';
import cx from 'classnames';
import { Tooltip } from 'react-tooltip';

/**
 * Internal dependencies
 */
import Loader from '@/assets/svg-icons/loader';
import SuccessTick from '@/assets/svg-icons/success-tick';
import Icon from '@/common/icon/icon';
import Badge from '../badge/badge';

const Input = forwardRef(
    (
        {
            type = 'text',
            name,
            value = '',
            className = '',
            autoComplete,
            maxLength = 2000,
            required,
            isFocused,
            handleChange = () => {},
            handleBlur = () => {},
            handleFocus = () => {},
            handleClear,
            placeholder,
            errorText,
            label,
            id,
            pattern,
            title,
            titleContent = '',
            truncateLabel = false,
            showErrorBorder = false,
            validation = false,
            delayFocus = false,
            loading = false,
            svgIcon = false,
            svgHandler = null,
            success = false,
            disabled = false,
            ignoreInnerValidation = false,
            tooltipMarkup,
            badge,
            helperText,
            unit,
            info,
            ...props
        },
        externalRef
    ) => {
        const input = useRef();
        const [isValid, setIsValid] = useState(true);
        const [showSuccess, setShowSucess] = useState(false);

        useEffect(() => {
            if (delayFocus) {
                setTimeout(() => {
                    input.current.focus();
                }, delayFocus);
            }

            if (isFocused && !delayFocus) {
                input.current.focus();
            }
        }, []);

        useEffect(() => {
            let successTickAnimation = null;

            if (success) {
                setShowSucess(true);
                successTickAnimation = setTimeout(() => setShowSucess(false), 3000);
            }

            return () => {
                if (success) {
                    clearTimeout(successTickAnimation);
                }
            };
        }, [success]);

        const handleValidation = (e) => {
            if (validation === 'cannot-be-empty') {
                setIsValid(e.target.value.length);
            }
        };

        return (
            <div
                className={cx('field', className, {
                    'field--tooltip': tooltipMarkup,
                    'field--unit': unit,
                    'field--with-badge': badge,
                    'field--error-border': showErrorBorder && errorText,
                    'field--titled': title,
                    'field--empty': !value,
                })}
            >
                {title && (
                    <span className="field__title">
                        {title}
                        {info && <span className="cms-node__info">{info}</span>}
                    </span>
                )}
                {titleContent}
                <input
                    type={type}
                    name={name}
                    id={id}
                    pattern={pattern}
                    value={value || ''}
                    placeholder={label ? label : placeholder}
                    className="field__input"
                    ref={externalRef ? externalRef : input}
                    autoComplete={autoComplete}
                    required={required}
                    disabled={disabled}
                    maxLength={maxLength}
                    onChange={(e) => {
                        handleValidation(e);
                        handleChange(e);
                    }}
                    onBlur={(e) => {
                        handleValidation(e);
                        handleBlur(e);
                    }}
                    onFocus={(e) => {
                        handleFocus(e);
                    }}
                    {...props}
                />

                {unit && (
                    <div className="field__unit">
                        <span className="field__unit-text">{unit}</span>
                    </div>
                )}

                {helperText && <div className="field__helper-text">{helperText}</div>}

                {label && (
                    <label
                        className={cx('field__label', {
                            'overflow-truncate': truncateLabel,
                        })}
                        htmlFor={id}
                    >
                        {label}
                    </label>
                )}

                {svgIcon && (
                    <span className="field__svg" onClick={svgHandler}>
                        {svgIcon}
                    </span>
                )}

                <button
                    className={cx('field__clear-cta', {
                        'field__clear-cta--visible': typeof handleClear === 'function',
                    })}
                    onClick={(e) => {
                        if (typeof handleClear === 'function') {
                            handleClear(e);
                        }

                        input?.current?.focus?.();
                    }}
                >
                    <Icon name="circle-xmark" />
                </button>

                {tooltipMarkup && (
                    <div className="field__tooltip">
                        <span data-tooltip-id="field-tooltip">
                            <Icon name="circle-question" color="#CCCCCC" size={14} />
                        </span>

                        <Tooltip
                            clickable={false}
                            className="tooltip tooltip--lg tooltip-commisssion"
                            id="field-tooltip"
                            place="top"
                        >
                            {tooltipMarkup}
                        </Tooltip>
                    </div>
                )}

                {badge && (
                    <Badge className={cx('field__badge', { 'field__badge--active': value })} {...badge.props}>
                        {badge.children}
                    </Badge>
                )}

                <span className="field__icon">
                    {loading ? <Loader width={32} /> : showSuccess ? <SuccessTick /> : null}
                </span>

                {ignoreInnerValidation
                    ? !!errorText && <p className="field__error">{errorText}</p>
                    : !isValid && !!errorText && <p className="field__error">{errorText}</p>}
            </div>
        );
    }
);

export default Input;
